import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, from as observableFrom } from 'rxjs';
import { filter, map, mergeMap, toArray } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { selectPolicies } from '@amfam/policy/data-access/lib/+state/policies/policies.selectors';
import { Policy } from '@amfam/policy/models/lib/models/policy';
import { BufferedFile } from '@amfam/shared/models/lib/e-signature/buffered-file';
import { SignatureRequest } from '@amfam/shared/models/lib/e-signature/signature-request';
import { selectUserState } from '@amfam/shared/user/lib/+state/user.selectors';
import { selectBrandState } from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { ContentService } from '@amfam/shared/utility/shared-services/lib/content/content.service';
import { CopyService } from '@amfam/shared/utility/shared-services/lib/copy/copy.service';

// On: route e-signature through the exp-user-account experience API. Off: the existing L7 endpoint.
const ESIGNATURE_EXP_API_FLAG = 'esignature-api-exp-api';

@Injectable({
  providedIn: 'root'
})
export class ESignatureService {
  private contentService = inject(ContentService);
  private http = inject(HttpClient);
  private store = inject(Store);
  private copyService = inject(CopyService);
  private featureFlagService = inject(FeatureFlagService);

  private get useExpApi(): boolean {
    return this.featureFlagService.isEnabled(ESIGNATURE_EXP_API_FLAG);
  }

  private get eSignatureBaseUrl(): string {
    return this.useExpApi ? `${env.userAccountApi}/esignature/` : env.eSignatureApi;
  }

  requiresSignature(): Observable<Policy[]> {
    const policySource = selectPolicies;
    return this.store.select(policySource).pipe(
      filter(policies => {
        for (const policy of policies) {
          if (policy && policy.policyAddress) {
            const eSignatureStates: string[] = this.copyService.getCopy(
              'goPaperless.eSignatureStates'
            );
            return eSignatureStates.includes(policy.policyAddress.state?.toUpperCase() ?? '');
          }
        }
        return false;
      })
    );
  }

  requestSignature(request: SignatureRequest): Observable<string> {
    return observableFrom(request.files).pipe(
      mergeMap(file => {
        const localFileOptions = {
          headers: new HttpHeaders({ Accept: file.contentType }),
          responseType: 'arraybuffer'
        };

        return this.contentService
          .getMedia(file.absolutePath, localFileOptions)
          .pipe(map(response => new BufferedFile(file, response)));
      }),
      toArray(),
      mergeMap(bufferedFiles => this.createDocusignEnvelope(request.callbackUrl, bufferedFiles))
    );
  }

  private createDocusignEnvelope(
    callbackUrl: string,
    files: Array<BufferedFile>
  ): Observable<string> {
    const brandData = this.store.selectSignal(selectBrandState)();
    const userData = this.store.selectSignal(selectUserState)();

    const context = brandData?.isPartner ? 'myaccount-whitelabel' : 'myaccount';
    const url = `${this.eSignatureBaseUrl}envelopes`;
    const multipartBoundary = `----=${Date.now().toString()}`;
    let headers = this.getHeaders();
    headers = headers
      .set('content-type', `multipart/form-data; boundary="${multipartBoundary}"`)
      .set('MIME-Version', '1.0');

    const data = {
      envelope: {
        contextName: context,
        callbackURL: callbackUrl,
        recipients: [
          {
            name: userData?.displayName,
            emailAddress: userData?.emailAddress,
            recipientType: 'Signer',
            signingMethod: 'Embedded',
            routingOrder: 1,
            roles: ['PrimaryInsured1']
          }
        ],
        documentDetails: files.map((file, index) => ({
          name: file.friendlyName,
          originalDocumentFileName: file.fileName,
          sequence: index + 1,
          producerId: '1234567890',
          customerId: '12345678901234'
        })),
        sender: [
          {
            senderId: 'myacct',
            senderIdType: 'system',
            senderRole: 'Sender'
          }
        ]
      }
    };
    const jsonData = [
      `--${multipartBoundary}`,
      'Content-Type: application/json; name=myaccount.json',
      'Content-Disposition: form-data; name="myaccount.json"; filename="myaccount.json"',
      '',
      JSON.stringify(data)
    ].join('\r\n');
    const filesWithHeaders = files.map(file => {
      const fileHeaders = [
        '',
        `--${multipartBoundary}`,
        `Content-Type: ${file.contentType}; name=${file.fileName}`,
        `Content-Disposition: form-data; name="${file.fileName}"; filename="${file.fileName}"`,
        '',
        ''
      ].join('\r\n');

      const fileDataView = new Uint8Array(file.buffer);
      return {
        binaryView: fileDataView,
        headers: fileHeaders,
        length: fileHeaders.length + fileDataView.byteLength
      };
    });
    const endMultipartBoundary = ['', `--${multipartBoundary}--`].join('\r\n');

    const filesLength = filesWithHeaders.reduce(
      (length, fileWithHeaders) => length + fileWithHeaders.length,
      0
    );
    const binaryDataSize = jsonData.length + filesLength + endMultipartBoundary.length;
    const binaryData = new Uint8Array(binaryDataSize);
    let i = 0;
    for (; i < jsonData.length; i++) {
      // eslint-disable-next-line no-bitwise
      binaryData[i] = jsonData.charCodeAt(i) & 0xff;
    }
    for (const fileWithHeaders of filesWithHeaders) {
      for (let j = 0; j < fileWithHeaders.headers.length; i++, j++) {
        // eslint-disable-next-line no-bitwise
        binaryData[i] = fileWithHeaders.headers.charCodeAt(j) & 0xff;
      }
      for (let j = 0; j < fileWithHeaders.binaryView.byteLength; i++, j++) {
        binaryData[i] = fileWithHeaders.binaryView[j] ?? 0;
      }
    }
    for (let j = 0; j < endMultipartBoundary.length; i++, j++) {
      // eslint-disable-next-line no-bitwise
      binaryData[i] = endMultipartBoundary.charCodeAt(j) & 0xff;
    }
    const body = binaryData.buffer;

    return this.http
      .post(url, body, { headers: headers })
      .pipe(mergeMap(json => this.getDocusignUrl(json)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getDocusignUrl(envelopeJson: any): Observable<string> {
    const envelope = envelopeJson.envelopes;
    const envelopeId = envelope.envelopeId;
    const recipientId = envelope.recipients.recipientId;
    const signingUrl = `${this.eSignatureBaseUrl}envelopes/${envelopeId}/recipients/${recipientId}/retrieveSigningUrl`;
    const signingBody = {
      authenticationAssertion: {
        assertionID: 123,
        authenticationDateTime: '2012-04-23T18:25:43.511Z',
        authenticationMethod: 'SingleSignOn_CASiteminder',
        securityDomain: 'Corporate'
      }
    };
    return this.http
      .post(signingUrl, signingBody, { headers: this.getHeaders() })
      .pipe(map(json => this.getSigningUrl(json)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getSigningUrl(json: any): string {
    const recipient = json.recipients;
    const redirectUrl = decodeURIComponent(recipient.signingUrl);

    return redirectUrl;
  }

  private getHeaders() {
    const headers = new HttpHeaders({
      authId: env.processId,
      userId: env.processId
    });
    return headers;
  }
}
